import { Link } from "react-router-dom";

import { useToasts, type Toast } from "@/store/toasts";

const KIND_STYLE: Record<Toast["kind"], string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  error: "border-red-200 bg-red-50 text-red-900",
  info: "border-slate-200 bg-white text-slate-800",
};

/**
 * Top-right stack of slide-in notifications.
 *
 * Used by the order flow (and anywhere else) to confirm an action
 * without yanking the user off the page they're on. Purchase
 * confirmations include a "View order" link the user can click on
 * their own time.
 */
export function Toaster() {
  const toasts = useToasts((s) => s.toasts);
  const dismiss = useToasts((s) => s.dismiss);

  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto rounded-md border px-4 py-3 shadow-card ${KIND_STYLE[t.kind]}`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 text-sm">
              <div className="font-semibold">{t.title}</div>
              {t.body && <div className="mt-1 text-xs opacity-80">{t.body}</div>}
              {t.link && (
                <Link
                  to={t.link.to}
                  onClick={() => dismiss(t.id)}
                  className="mt-2 inline-block text-xs font-semibold text-accent hover:underline"
                >
                  {t.link.label} →
                </Link>
              )}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              className="text-xs font-medium text-slate-400 hover:text-slate-700"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
